import { Helmet } from "react-helmet-async";

/**
 * Per-page <head> manager. Keeps every public route uniquely titled & described
 * for search engines while sharing sensible defaults.
 *
 * Usage:
 *   <SEO
 *     title="Help Center · Kiddo+"
 *     description="Step-by-step guides for every Kiddo+ feature…"
 *     path="/help-center"
 *   />
 *
 * Optional props:
 *   image     – absolute OG/Twitter image URL (1200x630)
 *   noindex   – set true to discourage indexing
 *   jsonLd    – object | array — emitted as JSON-LD <script>(s)
 *   keywords  – string for the `keywords` meta tag
 */
const SITE_URL = "https://kiddoplus.com";
const DEFAULT_IMAGE = "https://kiddoplus.com/og-image.jpg";

export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd,
  keywords,
  type = "website",
}) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title.includes("Kiddo+") ? title : `${title} | Kiddo+`;
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Kiddo+" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {ldArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

/* -------- shared schema helpers -------- */

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": it.name,
      "item": `${SITE_URL}${it.path}`,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
}

export function articleSchema({ title, description, path, image = DEFAULT_IMAGE, datePublished = "2026-05-13" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": image,
    "url": `${SITE_URL}${path}`,
    "datePublished": datePublished,
    "dateModified": datePublished,
    "author": { "@type": "Organization", "name": "Kiddo+" },
    "publisher": {
      "@type": "Organization",
      "name": "Kiddo+",
      "logo": { "@type": "ImageObject", "url": "https://kiddoplus.com/kiddo-logo-512.png" },
    },
  };
}
